import { useState, useEffect } from 'react'
import { Calendar, Plus, Edit2, Trash2, Save, X, Sun, AlertCircle } from 'lucide-react'
import { apiService } from '../services/api'

const MONTHS = [
  { value: 1, label: 'Styczeń' },
  { value: 2, label: 'Luty' },
  { value: 3, label: 'Marzec' },
  { value: 4, label: 'Kwiecień' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Czerwiec' },
  { value: 7, label: 'Lipiec' },
  { value: 8, label: 'Sierpień' },
  { value: 9, label: 'Wrzesień' },
  { value: 10, label: 'Październik' },
  { value: 11, label: 'Listopad' },
  { value: 12, label: 'Grudzień' }
]

const DAYS_OF_WEEK = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']

export default function DaysOffManagementPage() {
  const [daysOff, setDaysOff] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [showModal, setShowModal] = useState(false)
  const [editingDayOff, setEditingDayOff] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [generatingSundays, setGeneratingSundays] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    loadDaysOff()
  }, [selectedMonth, selectedYear])

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadDaysOff = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.getDaysOff({
        month: selectedMonth,
        year: selectedYear
      })
      
      setDaysOff(response.data.days_off)
    } catch (err) {
      setError('Nie udało się załadować dni wolnych')
      console.error('Error loading days off:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateDayOff = () => {
    setEditingDayOff(null)
    setShowModal(true)
  }

  const handleEditDayOff = (dayOff) => {
    setEditingDayOff(dayOff)
    setShowModal(true)
  }

  const handleDeleteDayOff = async (dayOff) => {
    try {
      await apiService.deleteDayOff(dayOff.id)
      await loadDaysOff()
      setDeleteConfirm(null)
      setSuccessMessage('Dzień wolny został usunięty')
    } catch (err) {
      console.error('Error deleting day off:', err)
      alert('Błąd podczas usuwania dnia wolnego: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleSaveDayOff = async (dayOffData) => {
    try {
      if (editingDayOff) {
        await apiService.updateDayOff(editingDayOff.id, {
          dayoffdescription: dayOffData.dayoffdescription
        })
        setSuccessMessage('Dzień wolny został zaktualizowany')
      } else {
        await apiService.createDayOff(dayOffData)
        setSuccessMessage('Dzień wolny został dodany')
      }
      await loadDaysOff()
      setShowModal(false)
      setEditingDayOff(null)
    } catch (err) {
      console.error('Error saving day off:', err)
      const errorMessage = err.response?.data?.detail || err.message
      
      // Show duplicate error in modal
      if (errorMessage.includes('już istnieje')) {
        throw new Error('Data już istnieje w systemie')
      } else {
        alert('Błąd podczas zapisywania dnia wolnego: ' + errorMessage)
      }
    }
  }

  const handleGenerateSundays = async () => {
    if (!confirm(`Czy na pewno chcesz wygenerować wszystkie niedziele dla ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}?`)) {
      return
    }

    try {
      setGeneratingSundays(true)
      const response = await apiService.generateSundays(selectedMonth, selectedYear)
      setSuccessMessage(response.data.message)
      await loadDaysOff()
    } catch (err) {
      console.error('Error generating Sundays:', err)
      alert('Błąd podczas generowania niedziel: ' + (err.response?.data?.detail || err.message))
    } finally {
      setGeneratingSundays(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pl-PL', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })
  }

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString)
    return DAYS_OF_WEEK[date.getDay()]
  }

  // Generate years: current year - 1 to current year + 2
  const currentYear = new Date().getFullYear()
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-8 h-8 text-blue-600" />
              Zarządzanie dniami wolnymi
            </h1>
            <p className="text-gray-600 mt-1">
              Zarządzanie świętami, niedzielami i innymi dniami wolnymi
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleGenerateSundays}
              disabled={generatingSundays}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Wygeneruj wszystkie niedziele w wybranym miesiącu"
            >
              <Sun className="w-5 h-5" />
              {generatingSundays ? 'Generowanie...' : 'Wygeneruj niedziele'}
            </button>
            <button
              onClick={handleCreateDayOff}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Dodaj dzień wolny
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Month selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Miesiąc
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rok
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Days Off Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Ładowanie...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            {error}
          </div>
        ) : daysOff.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>Brak dni wolnych dla wybranego okresu</p>
            <p className="text-sm mt-1">Kliknij "Wygeneruj niedziele" aby automatycznie dodać niedziele</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dzień tygodnia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {daysOff.map((dayOff) => (
                  <tr key={dayOff.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {formatDate(dayOff.dayoff)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {getDayOfWeek(dayOff.dayoff)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {dayOff.dayoffdescription || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditDayOff(dayOff)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edytuj"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(dayOff)}
                        className="text-red-600 hover:text-red-900"
                        title="Usuń"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <DayOffModal
          dayOff={editingDayOff}
          onSave={handleSaveDayOff}
          onClose={() => {
            setShowModal(false)
            setEditingDayOff(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Potwierdzenie usunięcia
            </h3>
            <p className="text-gray-600 mb-6">
              Czy na pewno chcesz usunąć dzień wolny {formatDate(deleteConfirm.dayoff)}
              {deleteConfirm.dayoffdescription && (
                <> - {deleteConfirm.dayoffdescription}</>
              )}?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Anuluj
              </button>
              <button
                onClick={() => handleDeleteDayOff(deleteConfirm)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Day Off Modal Component
function DayOffModal({ dayOff, onSave, onClose }) {
  const [formData, setFormData] = useState({
    dayoff: dayOff?.dayoff || new Date().toISOString().split('T')[0],
    dayoffdescription: dayOff?.dayoffdescription || ''
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const validate = () => {
    const newErrors = {}
    
    if (!formData.dayoff) {
      newErrors.dayoff = 'Data jest wymagana'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validate()) {
      return
    }
    
    try {
      setSubmitting(true)
      await onSave({
        dayoff: formData.dayoff,
        dayoffdescription: formData.dayoffdescription || null
      })
    } catch (err) {
      // Show error in form
      if (err.message.includes('już istnieje')) {
        setErrors({ dayoff: err.message })
      } else {
        setErrors({ general: err.message })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {dayOff ? 'Edytuj dzień wolny' : 'Dodaj nowy dzień wolny'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {errors.general && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data *
              </label>
              <input
                type="date"
                value={formData.dayoff}
                onChange={(e) => setFormData({ ...formData, dayoff: e.target.value })}
                disabled={!!dayOff}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.dayoff ? 'border-red-500' : 'border-gray-300'
                } ${dayOff ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
                required
              />
              {errors.dayoff && (
                <p className="mt-1 text-sm text-red-600">{errors.dayoff}</p>
              )}
              {dayOff && (
                <p className="mt-1 text-xs text-gray-500">
                  Data nie może być zmieniona. Usuń i utwórz nowy wpis aby zmienić datę.
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opis (opcjonalny)
              </label>
              <input
                type="text"
                value={formData.dayoffdescription}
                onChange={(e) => setFormData({ ...formData, dayoffdescription: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="np. Święto Bożego Narodzenia"
                maxLength={255}
              />
              <p className="mt-1 text-xs text-gray-500">
                Wprowadź nazwę święta lub inne informacje o dniu wolnym
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {submitting ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}






